/**
 * Word cloud component built with d3-cloud.
 *
 * Renders a frequency-weighted word cloud with mixed rotations, staggered
 * entrance animation, hover effects, opacity variation, and a supplementary
 * data table. Uses a curated professional colour palette that overrides
 * AI-assigned colours.
 */

import { useRef, useEffect, useCallback, useMemo } from "react";
import * as d3 from "d3";
import cloud from "d3-cloud";
import type { WordCloudConfig } from "../../types/index.js";
import {
	positionTooltip,
	formatNumber,
	useResizeObserver,
} from "./chartUtils.js";
import { useRenderLoopGuard } from "../../hooks/useRenderLoopGuard.js";

interface WordCloudViewProps {
	config: WordCloudConfig;
	isModal?: boolean;
}

/**
 * Compute canvas height based on word count.
 * Sparse clouds get a shorter canvas to avoid excessive empty space.
 */
function computeCloudHeight(wordCount: number): number {
	if (wordCount < 10) return 200;
	if (wordCount < 15) return 260;
	return 320;
}

/**
 * Font family used for both d3-cloud layout measurement and SVG rendering.
 * Must be identical in both places; a mismatch causes words to overflow
 * their measured bounds and overlap each other.
 */
const CLOUD_FONT = "DM Sans, system-ui, sans-serif";

/** Hard cap on words rendered regardless of maxWords setting. */
const WORD_HARD_CAP = 80;

/**
 * Curated word cloud palette — harmonious, WCAG AA compliant on white.
 * Overrides AI-assigned colours for visual consistency.
 */
const WORD_CLOUD_PALETTE = [
	"#1B5E8A",  // deep ocean blue
	"#D95F4E",  // warm coral
	"#0E7C6B",  // dark teal
	"#C27D2A",  // burnt amber
	"#7A3EB5",  // rich purple
	"#2E8B57",  // sea green
	"#B74C78",  // dusty rose
	"#4A6FA5",  // steel blue
	"#8B6914",  // dark goldenrod
	"#6B5B95",  // muted violet
	"#C0563B",  // terracotta
	"#2D7D9A",  // cerulean
];

/**
 * Returns a rotation angle for a word. Produces an exact mix using modulo-20:
 * 70% horizontal (0 degrees, buckets 0–13), 15% vertical (+90°, buckets 14–16),
 * 15% vertical (-90°, buckets 17–19).
 */
function pickRotation(index: number): number {
	const bucket = index % 20;
	if (bucket < 14) return 0;   // 70% horizontal
	if (bucket < 17) return 90;  // 15% vertical (+90°)
	return -90;                  // 15% vertical (-90°)
}

export default function WordCloudView({ config, isModal = false }: WordCloudViewProps) {
	useRenderLoopGuard("WordCloudView");
	const svgRef = useRef<SVGSVGElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);
	const { width: containerWidth, height: containerHeight } = useResizeObserver(containerRef);

	const { words, maxWords = 60 } = config;

	// Compute canvas height before the d3-cloud layout runs — sparse clouds get less height.
	// Use container height only when explicitly in modal context (isModal=true).
	// For inline charts the container height is content-driven, so using it
	// would create a feedback loop: cloud grows → container grows → cloud grows…
	const effectiveWordCount = Math.min((words ?? []).length, Math.min(maxWords, WORD_HARD_CAP));
	const baseCloudHeight = computeCloudHeight(effectiveWordCount);
	// Reserve approx 160px for the data table below the cloud when in modal context.
	const effectiveContainerHeight = isModal ? containerHeight : 0;
	const cloudHeight = effectiveContainerHeight > 0 ? Math.max(baseCloudHeight, effectiveContainerHeight - 160) : baseCloudHeight;

	// Whether any word carries a category — drives the conditional Category column.
	const hasCategories = useMemo(() => (words ?? []).some((w) => Boolean(w.category)), [words]);

	// Sorted words for the data table (descending by value).
	// colourMap is keyed by slice index (same as the cloud) so swatches match exactly.
	const sortedWords = useMemo(() => {
		if (!words || words.length === 0) return [];
		const effectiveCap = Math.min(maxWords, WORD_HARD_CAP);
		const sliced = words.slice(0, effectiveCap);
		const colourMap = new Map(sliced.map((w, i) => [w.text, WORD_CLOUD_PALETTE[i % WORD_CLOUD_PALETTE.length]]));
		return [...sliced]
			.sort((a, b) => b.value - a.value)
			.map(w => ({ ...w, colour: colourMap.get(w.text)! }));
	}, [words, maxWords]);

	const renderChart = useCallback(() => {
		if (!svgRef.current || !words || words.length === 0 || containerWidth === 0) return;

		const width = containerWidth;
		const height = cloudHeight;

		// Truncate word list to effective cap
		const effectiveCap = Math.min(maxWords, WORD_HARD_CAP);
		const wordSlice = words.slice(0, effectiveCap);

		// Compute domain for font size scale
		const values = wordSlice.map((w) => w.value);
		const minValue = d3.min(values) ?? 1;
		const maxValue = d3.max(values) ?? 1;

		// Square-root scale with a narrower range so the largest word does not
		// dominate excessively and smaller words remain legible.
		const fontScale = d3
			.scaleSqrt()
			.domain([minValue, maxValue])
			.range(minValue === maxValue ? [28, 28] : [14, 58]);

		// Detect if user prefers reduced motion
		const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		type PlacedWord = {
			text: string;
			value: number;
			size: number;
			x?: number;
			y?: number;
			rotate?: number;
			colour: string;
			opacity: number;
		};

		const draw = (placedWords: PlacedWord[]) => {
			if (!svgRef.current) return;

			const svg = d3.select(svgRef.current);
			svg.selectAll("*").remove();

			svg
				.attr("width", width)
				.attr("height", height)          // intrinsic size for SVG export
				.style("height", `${height}px`)  // CSS override: beats initial height:0 on SVG element
				.attr("role", "img")
				.attr("aria-label", "Word cloud visualisation showing relative word frequency by size");

			// Centre the cloud group within the SVG canvas
			const g = svg
				.append("g")
				.attr("transform", `translate(${width / 2},${height / 2})`);

			// Bind word data and build text elements
			g.selectAll<SVGTextElement, PlacedWord>("text")
				.data(placedWords)
				.join("text")
				.attr("transform", (d) => `translate(${d.x ?? 0},${d.y ?? 0}) rotate(${d.rotate ?? 0}) scale(1)`)
				.attr("text-anchor", "middle")
				.attr("dominant-baseline", "central")
				.attr("font-family", CLOUD_FONT)
				.attr("font-size", (d) => `${d.size ?? 14}px`)
				.attr("font-weight", (d) => (d.size > 40 ? 700 : d.size > 24 ? 600 : 500))
				.attr("fill", (d) => d.colour)
				.attr("cursor", "default")
				.style("user-select", "none")
				.style("filter", (d) => {
					// Subtle text shadow for depth — stronger on larger words
					const blur = d.size > 36 ? 2 : 1;
					return `drop-shadow(0 1px ${blur}px rgba(0,0,0,0.08))`;
				})
				// Start transparent; staggered fade-in applied via transition below
				.style("opacity", 0)
				.text((d) => d.text ?? "")
				.on("mouseenter", function (event: MouseEvent, d: PlacedWord) {
					if (tooltipRef.current) {
						tooltipRef.current.innerHTML = `<strong>${d.text}</strong><br/>Mentions: ${formatNumber(d.value)}`;
						tooltipRef.current.style.opacity = "1";
						positionTooltip(tooltipRef.current, event);
					}
					// Scale up on hover for tactile feedback
					d3.select(this)
						.attr("transform", `translate(${d.x ?? 0},${d.y ?? 0}) rotate(${d.rotate ?? 0}) scale(1.18)`)
						.attr("fill", d.colour)
						.style("filter", `drop-shadow(0 2px 4px rgba(0,0,0,0.18)) brightness(1.1)`);
				})
				.on("mousemove", function (event: MouseEvent) {
					if (tooltipRef.current) positionTooltip(tooltipRef.current, event);
				})
				.on("mouseleave", function (_, d: PlacedWord) {
					if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
					const blur = d.size > 36 ? 2 : 1;
					d3.select(this)
						.attr("transform", `translate(${d.x ?? 0},${d.y ?? 0}) rotate(${d.rotate ?? 0}) scale(1)`)
						.style("filter", `drop-shadow(0 1px ${blur}px rgba(0,0,0,0.08))`);
				})
				// Staggered entrance — each word fades in with scale
				.transition()
				.duration(prefersReducedMotion ? 0 : 500)
				.delay((_, i: number) => (prefersReducedMotion ? 0 : i * 15))
				.ease(d3.easeCubicOut)
				.style("opacity", (d: PlacedWord) => d.opacity);
		};

		// Compute opacity based on value — higher value = more opaque
		const opacityScale = d3
			.scaleLinear()
			.domain([minValue, maxValue])
			.range(minValue === maxValue ? [1, 1] : [0.6, 1]);

		cloud<PlacedWord>()
			.size([width, height])
			.words(
				wordSlice.map((w, i) => ({
					text: w.text,
					size: fontScale(w.value),
					colour: WORD_CLOUD_PALETTE[i % WORD_CLOUD_PALETTE.length],
					value: w.value,
					opacity: opacityScale(w.value),
				}))
			)
			.padding(2)              // Tight padding for dense packing
			.spiral("archimedean")   // Archimedean spiral with tight step
			.rotate((_, i) => pickRotation(i))
			.font(CLOUD_FONT)        // Must match attr("font-family") in draw() to avoid overlap
			.fontSize((d) => d.size as number)
			.on("end", draw)
			.start();
	}, [words, maxWords, containerWidth, containerHeight, isModal]);

	useEffect(() => {
		renderChart();
	}, [renderChart]);

	if (!words || words.length === 0) {
		return (
			<div className="chart-empty-state" role="status">
				No data available
			</div>
		);
	}

	return (
		<div ref={containerRef} style={{ position: "relative", width: "100%", height: "100%" }}>
			{/* height: 0 prevents the browser default 150px SVG height before d3-cloud renders.
			     renderChart sets .style("height") to override this once layout is complete. */}
			<svg ref={svgRef} style={{ display: "block", width: "100%", height: 0 }} />

			{/* Tooltip */}
			<div
				ref={tooltipRef}
				className="chart-tooltip"
				role="tooltip"
				style={{ opacity: 0 }}
			/>

			{/* Data table — compact supplementary view */}
			{sortedWords.length > 0 && (
				<div className="wordcloud-table-wrapper">
					<table className="wordcloud-table" aria-label="Word frequency data">
						<thead>
							<tr>
								<th scope="col" className="wordcloud-table__rank">#</th>
								<th scope="col" className="wordcloud-table__word">Word</th>
								{hasCategories && (
									<th scope="col" className="wordcloud-table__category">Category</th>
								)}
								<th scope="col" className="wordcloud-table__value">Count</th>
							</tr>
						</thead>
						<tbody>
							{sortedWords.map((w, i) => (
								<tr key={w.text}>
									<td className="wordcloud-table__rank">{i + 1}</td>
									<td className="wordcloud-table__word">
										<span className="wordcloud-table__word-inner">
											<span
												className="wordcloud-table__swatch"
												style={{ background: w.colour }}
											/>
											{w.text}
										</span>
									</td>
									{hasCategories && (
										<td className="wordcloud-table__category">
											{w.category ?? "–"}
										</td>
									)}
									<td className="wordcloud-table__value">{formatNumber(w.value)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
